import type Highcharts from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import type { Setup } from "../../../../api/useSetups";
import t from "../../../../i18n";
import { useCanUserDoOnProject } from "../../../canUserDo";
import useIdle from "../../../../lib/hooks/useIdle";
import ChartCard from "@ui/Highcharts/ui/Card";
import Selector from "@ui/Selector";
import Switch from "@ui/Switch";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import Legend from "./Legend";
import Observables from "./Observables";
import type { ObservableOrder } from "./Observables/useOptions";
import QuantitiesOfInterest from "./QuantitiesOfInterest";
import { useFlagSelectedQuantities } from "./hook/useCommonOptions";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { AnalysisDateTitle } from "../../AnalysisDateTitle";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import { useShowInactiveObservables } from "./hook/useShowInactiveObservables";
import { useDiagnosisFailureOrder } from "./hook/useDiagnosisFailureOrder";
import { css, cx } from "@linaria/core";
import AnalysisFilterCompliant from "../../Analysis/AnalysisFilterCompliant";
import IndicatorsDisplay from "../../Analysis/IndicatorDisplay";
import { type Quantity } from "../../../../api/useQuantities";
import Text, { H4 } from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import { fonts } from "@ui";
import { InformationLine, SortAlphabetAsc, SortDesc } from "@ui/IconV2/react";
import { isAnalysisQuantityValid } from "../../metrology";

const orderOptionsBase: {
  label: string;
  value: ObservableOrder;
  description: string;
}[] = [
  {
    label: t("diagnosis.title.observables.order.decreasing.impact"),
    value: "DECREASING_IMPACT_ORDER",
    description: t("diagnosis.title.observables.order.decreasing.impact.description"),
  },
  {
    label: t("diagnosis.title.observables.order.alphabetical"),
    value: "ALPHABETICAL_ORDER",
    description: t("diagnosis.title.observables.order.alphabetical.description"),
  },
];

interface P {
  visibleSymptomHistogramChartId: string;
  project: Project;
  quantities: Quantity[];
  setups: Setup[];
  analysis: ActiveAnalysis;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
}

const DiagnosisSymptomAndImpacts = ({
  visibleSymptomHistogramChartId,
  project,
  quantities,
  setups,
  analysis,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
}: P) => {
  const idle = useIdle(300);
  const canUpdateFailureValues = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const [order, setOrder] = useDiagnosisFailureOrder({ scope: project.id });
  const [flagSelectedQuantities, setFlagSelectedQuantities] = useFlagSelectedQuantities({
    scope: project.id,
  });
  const toggleFlagSelectedQuantities = React.useCallback(
    () => setFlagSelectedQuantities(v => !v),
    [setFlagSelectedQuantities],
  );
  const [showInactiveObservables, setShowInactiveObservables] = useShowInactiveObservables({
    scope: project.id,
  });
  const toggleShowAllImpacts = React.useCallback(
    () => setShowInactiveObservables(v => !v),
    [setShowInactiveObservables],
  );

  const observableChartRef = React.useRef<Highcharts.Chart>(null);

  const setup = getAnalysisSetup(setups, analysis);
  const inputs = setup?.diagnosisInputs;
  const hasInactiveObservables = React.useMemo(
    () =>
      inputs
        ?.filter(input => quantities.some(quantity => quantity.id === input.quantityId))
        .some(observable => observable.enabled === false),
    [inputs, quantities],
  );

  const processIndicators = React.useMemo(
    () =>
      quantities
        .filter(q => q.processIndicatorOrder !== null)
        .sort((a, b) => (a.processIndicatorOrder ?? 0) - (b.processIndicatorOrder ?? 0)),
    [quantities],
  );

  const indicatorQuantityValues = React.useMemo(() => {
    const validProcessIndicators = processIndicators.filter(quantity =>
      isAnalysisQuantityValid(analysis, quantity),
    );

    return Object.fromEntries(
      Object.entries(analysis.quantityValues).filter(([key]) =>
        validProcessIndicators.some(q => q.id === key),
      ),
    );
  }, [processIndicators, analysis]);

  const indicators = React.useMemo(
    () =>
      processIndicators.map(indicator => ({
        name: indicator.name,
        measured: indicatorQuantityValues[indicator.id]?.measured ?? null,
        unit: indicator.unit || "",
      })),
    [processIndicators, indicatorQuantityValues],
  );

  const observableCount = React.useMemo(() => {
    const list = inputs ?? [];
    return list.filter(input => {
      const hasQuantity = quantities.some(q => q.id === input.quantityId);
      if (!hasQuantity) return false;
      return showInactiveObservables ? true : input.enabled;
    }).length;
  }, [inputs, quantities, showInactiveObservables]);

  const orderOptionsWithIcons = React.useMemo(
    () =>
      orderOptionsBase.map((opt, i) => ({
        ...opt,
        label: "" as string,
        title: opt.label,
        tooltipText: opt.description,
        icon: i === 0 ? <SortDesc /> : <SortAlphabetAsc />,
      })),
    [],
  );

  const sortSelector = React.useMemo(
    () => <Selector options={orderOptionsWithIcons} value={order} onChange={setOrder} />,
    [order, setOrder, orderOptionsWithIcons],
  );

  const observablesToggle = React.useMemo(() => {
    if (!hasInactiveObservables) return null;

    return (
      <div>
        <Switch
          className={styledSwitch}
          checked={showInactiveObservables}
          onChange={toggleShowAllImpacts}
        >
          <Text>{t("diagnosis.chart.show_deactivated_observables")}</Text>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                className={styledOverlayWrapper}
                text={<div>{t("diagnosis.chart.show_deactivated_observables.tooltip")}</div>}
              />
            }
          >
            <span className={centerIcon}>
              <InformationLine />
            </span>
          </TooltipV2>
        </Switch>
      </div>
    );
  }, [hasInactiveObservables, showInactiveObservables, toggleShowAllImpacts]);

  const selectedQuantitiesToggle = React.useMemo(
    () => (
      <div>
        <Switch checked={flagSelectedQuantities} onChange={toggleFlagSelectedQuantities}>
          <Text>{t("diagnosis.chart.flag_selected_quantities")}</Text>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                text={t("diagnosis.chart.flag_selected_quantities.tooltip.description")}
              />
            }
          >
            <span className={centerIcon}>
              <InformationLine />
            </span>
          </TooltipV2>
        </Switch>
      </div>
    ),
    [flagSelectedQuantities, toggleFlagSelectedQuantities],
  );

  const performanceIndicatorQuantities = React.useMemo(
    () => quantities.filter(q => q.qoiOrder !== null),
    [quantities],
  );

  const hasPI = React.useMemo(
    () => quantities.filter(q => q.qoiOrder !== null).length > 0,
    [quantities],
  );

  const legend = React.useMemo(
    () => <Legend project={project} analysis={analysis} />,
    [analysis, project],
  );

  const performanceIndicatorCount = performanceIndicatorQuantities.length;
  const qoiTitle =
    performanceIndicatorCount === 0
      ? t("diagnosis.charts.symptoms.title.qoi")
      : performanceIndicatorCount === 1
        ? t("diagnosis.charts.symptoms.title.qoi.one")
        : t("diagnosis.charts.symptoms.title.qoi.many", { count: performanceIndicatorCount });
  const observablesTitle =
    observableCount === 0
      ? t("diagnosis.charts.symptoms.title.obs")
      : observableCount === 1
        ? t("diagnosis.charts.symptoms.title.obs.one")
        : t("diagnosis.charts.symptoms.title.obs.many", { count: observableCount });

  const children = React.useMemo(
    () => (
      <div className={charts}>
        {setup && (
          <div className={performanceIndicators} data-cy="qoi-chart">
            {hasPI && <H4 className={cx(chartsTitle, performanceIndicatorsTitle)}>{qoiTitle}</H4>}
            <QuantitiesOfInterest
              project={project}
              quantities={quantities}
              analysis={analysis}
              indexedFailureValues={indexedFailureValues}
              indexedFailureMetadata={indexedFailureMetadata}
              indexedManualFailureValues={indexedManualFailureValues}
              canUpdateFailureValues={canUpdateFailureValues}
              observableChartRef={observableChartRef}
              setups={setups}
              setup={setup}
              visibleSymptomHistogramChartId={visibleSymptomHistogramChartId}
            />
          </div>
        )}
        <div className={observables}>
          <div className={observablesHeader}>
            <H4 className={chartsTitle}>{observablesTitle}</H4>
            {sortSelector}
            {observablesToggle}
            {selectedQuantitiesToggle}
          </div>
          <Observables
            visibleSymptomHistogramChartId={visibleSymptomHistogramChartId}
            project={project}
            setups={setups}
            analysis={analysis}
            indexedFailureMetadata={indexedFailureMetadata}
            indexedFailureValues={indexedFailureValues}
            indexedManualFailureValues={indexedManualFailureValues}
            quantities={quantities}
            order={order}
            observableChartRef={observableChartRef}
            showInactiveObservables={showInactiveObservables}
          />
        </div>
      </div>
    ),
    [
      setup,
      hasPI,
      qoiTitle,
      observablesTitle,
      sortSelector,
      observablesToggle,
      selectedQuantitiesToggle,
      project,
      quantities,
      analysis,
      indexedFailureValues,
      indexedFailureMetadata,
      indexedManualFailureValues,
      canUpdateFailureValues,
      setups,
      visibleSymptomHistogramChartId,
      order,
      showInactiveObservables,
    ],
  );

  // defer rendering of below-the-fold graph
  if (!idle) return null;

  return (
    <ChartCard
      title={
        <div className={chartCardTitle}>
          <AnalysisDateTitle event={null} analysis={analysis} timeZone={project.timezone} />
          <AnalysisFilterCompliant project={project} quantities={quantities} analysis={analysis} />
          <IndicatorsDisplay indicators={indicators} />
        </div>
      }
      legend={legend}
    >
      {children}
    </ChartCard>
  );
};

export default React.memo(DiagnosisSymptomAndImpacts);

const charts = css`
  display: flex;
  /* flex-wrap: wrap; */
`;
const chartCardTitle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const chartsTitle = css`
  color: ${colors.carbon900};
  margin-bottom: ${spacings.xs};
  font: ${fonts.body1SemiBold};
`;
const performanceIndicatorsTitle = css`
  grid-column: 2 / span 2;
  grid-row: 1 / 2;
  padding-top: 5px;
`;
const performanceIndicators = css`
  flex-grow: 1;
  flex-shrink: 0;

  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, 1fr);
  max-width: 40%;

  &:not(:has(.${performanceIndicatorsTitle})) {
    display: block;
  }

  > :not(.${performanceIndicatorsTitle}) {
    grid-row: 2;
  }
`;
const styledSwitch = css`
  & > span {
    display: flex;
    align-items: center;
  }
`;
const centerIcon = css`
  padding-left: ${spacings.xxs};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
const styledOverlayWrapper = css`
  max-width: 350px;
`;
const observablesHeader = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  column-gap: 16px;
  row-gap: 8px;
  margin-bottom: ${spacings.xs};
`;
const observables = css`
  width: 100%;
  overflow: hidden;

  .${performanceIndicators} + & {
    margin-left: ${spacings.m};
    padding-left: ${spacings.m};
    border-left: 1px solid ${colors.titanium200};
  }

  .${observablesHeader} .${chartsTitle} {
    margin-bottom: 0;
    align-self: center;
  }

  .${chartsTitle} {
    align-self: flex-start;
  }
`;
